import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import { GridActionsCellItem, GridRowModes } from '@mui/x-data-grid';
import {
    Backdrop,
    Box,
    Drawer,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';

import DataGridTable from '../DataGrid';
import InfoIcon from '../Widgets/InfoIcon';
import tableBodyicon from '../../Assets/images/tablebody_edit.png';
import tableEditicon from '../../Assets/images/tableheader_edit.png';
import closeIcon from '../../Assets/images/close_red.png';
import LoaderIcon from '../../Assets/images/loader.svg';
import history from '../../Utility/history';
import { handleValidateCodevalue } from '../../Utility/constant';
import { createRequestData } from '../../Utility/apiHelper';
import { REQUEST_UPDATE_HASH_MAP } from '../../Store/Internationalise/type';
import { i18nRegistry } from '../../Utility/i18nRegistry';
import './i18nInspector.css';

/**
 * PagePanel Drawer Component
 */
function PagePanel({ open, onClose }) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const currentUser = useSelector((state) => state?.owner?.currentuser);
    const internationalise = useSelector((state) => state?.internationalise?.currentHashMapData);

    const drawerTitle = handleValidateCodevalue(internationalise, 'CONF_I18_DRAWER_TITLE');
    const infoText = handleValidateCodevalue(internationalise, 'CONF_I18_INFO_TEXT');

    const [entries, setEntries] = useState([]);
    const [rowModesModel, setRowModesModel] = useState({});
    const [isTableLoading, setIsTableLoading] = useState(true);

    // Populate entries from registry when drawer opens
    useEffect(() => {
        if (open) {
            setIsTableLoading(true);

            // Register drawer title & info keys so they are editable in the table
            i18nRegistry.register(drawerTitle, 'CONF_I18_DRAWER_TITLE');
            i18nRegistry.register(infoText, 'CONF_I18_INFO_TEXT');

            const all = i18nRegistry.getAll();
            setEntries(all);
            setIsTableLoading(false);
        }
    }, [open, drawerTitle, infoText]);

    // Row mode handlers
    const handleSaveClick = (id) => () => {
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.View } }));
    };

    const handleEditClick = (id) => () => {
        setRowModesModel((prev) => ({ ...prev, [id]: { mode: GridRowModes.Edit } }));
    };

    const handleCancelClick = (id) => () => {
        setRowModesModel((prev) => ({
            ...prev,
            [id]: { mode: GridRowModes.View, ignoreModifications: true },
        }));
    };

    // Process row update (called by DataGridTable when row is saved)
    const handleUpdateRecord = async (newRow) => {
        const trimmedCode = (newRow?.code || '').trim();
        const trimmedValue = (newRow?.value || '').trim();

        const payload = {
            clientId: currentUser?.client?.id,
            code: trimmedCode,
            status: 'ACTIVE',
            value: trimmedValue,
        };

        setIsTableLoading(true);
        try {
            // Call the API directly — avoids dispatching REQUEST_GLOBALIZE_DATA which
            // triggers the full-page loader visible behind the drawer.
            const response = await createRequestData(payload, `${process.env.REACT_APP_BASE_URL}i18`);

            if (response?.status === 200) {
                enqueueSnackbar(handleValidateCodevalue(internationalise, 'COMMON_UPDATED_SUCCESSFULLY'), {
                    variant: 'success',
                    className: 'i18n-snackbar-front',
                });

                // Immediately update Redux store so UI on current page reflects new value
                dispatch({
                    type: REQUEST_UPDATE_HASH_MAP,
                    payload: {
                        ...internationalise,
                        [trimmedCode]: trimmedValue,
                    },
                });

                // Update in-memory registry
                i18nRegistry.update(trimmedCode, trimmedValue);

                // Update local rows
                setEntries((prev) =>
                    prev.map((item) => (item.key === trimmedCode ? { ...item, value: trimmedValue } : item))
                );
            } else {
                enqueueSnackbar(handleValidateCodevalue(internationalise, 'COMMON_PLEASE_TRY_AGAIN'), {
                    variant: 'error',
                    className: 'i18n-snackbar-front',
                });
            }
        } catch (error) {
            enqueueSnackbar(handleValidateCodevalue(internationalise, 'COMMON_PLEASE_TRY_AGAIN'), {
                variant: 'error',
                className: 'i18n-snackbar-front',
            });
        } finally {
            setIsTableLoading(false);
        }
    };

    // Columns matching Internationalise page style exactly
    const columns = useMemo(
        () => [
            {
                field: 'actions',
                type: 'actions',
                headerName: (
                    <Tooltip title={handleValidateCodevalue(internationalise, 'COMMON_ACTION')}>
                        <img src={tableEditicon} alt="action" className="i18n-table-header-action-img" />
                    </Tooltip>
                ),
                width: 95,
                cellClassName: 'actions',
                getActions: ({ id }) => {
                    const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

                    if (isInEditMode) {
                        return [
                            <GridActionsCellItem
                                icon={<CheckIcon />}
                                label={handleValidateCodevalue(internationalise, 'COMMON_SAVE')}
                                className="textPrimary"
                                onClick={handleSaveClick(id)}
                            />,
                            <GridActionsCellItem
                                icon={<CancelIcon />}
                                label={handleValidateCodevalue(internationalise, 'COMMON_CANCEL')}
                                className="textPrimary"
                                onClick={handleCancelClick(id)}
                                color="inherit"
                            />,
                        ];
                    }

                    return [
                        <GridActionsCellItem
                            icon={<img src={tableBodyicon} alt="edit" />}
                            label={handleValidateCodevalue(internationalise, 'COMMON_EDIT')}
                            className="textPrimary"
                            onClick={handleEditClick(id)}
                            color="inherit"
                        />,
                    ];
                },
            },
            {
                field: 'code',
                headerName: handleValidateCodevalue(internationalise, 'CONF_I18_SEARCH_CODE'),
                width: 300,
                editable: false,
                renderCell: (params) => (
                    <span className="i18n-code-text" title={params.value}>
                        {params.value}
                    </span>
                ),
            },
            {
                field: 'value',
                headerName: handleValidateCodevalue(internationalise, 'CONF_I18_SEARCH_VALUE'),
                flex: 1,
                minWidth: 250,
                editable: true,
            },
        ],
        [rowModesModel, internationalise]
    );

    // Prepare row data for DataGridTable
    const dataGridData = useMemo(() => {
        return entries.map((item) => ({
            id: item.key,
            code: item.key,
            value: item.value || '',
        }));
    }, [entries]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            className="i18n-drawer loader-excluded"
            PaperProps={{
                className: 'i18n-drawer-paper',
            }}
        >
            {/* Header */}
            <Box className="i18n-drawer-header">
                <Box className="i18n-drawer-header-title-box">
                    <LanguageIcon className="i18n-drawer-header-icon" />
                    <Typography variant="subtitle1" className="i18n-drawer-header-title">
                        {drawerTitle}
                    </Typography>
                </Box>
                <Box className="i18n-drawer-header-actions">
                    <IconButton size="small" onClick={onClose} className="i18n-close-btn">
                        <img src={closeIcon} alt="close" className="i18n-close-icon-img" />
                    </IconButton>
                </Box>
            </Box>

            {/* Official Biztantu DataGridTable with built-in search toolbar, no Show Columns button */}
            <Box className="i18n-table-container">
                {/* <Box className="i18n-table-count-bar">
                    <Typography className="i18n-table-count-text">
                        {entries.length} key{entries.length !== 1 ? 's' : ''} on current page
                    </Typography>
                </Box> */}
                <Box className="i18n-info-bar">
                    <InfoIcon text="CONF_I18_INFO_TEXT" />
                </Box>
                <Box className="i18n-table-area">
                    <Backdrop
                        open={isTableLoading}
                        className="i18n-table-backdrop"
                    >
                        <div>
                            <img
                                src={LoaderIcon}
                                alt="loading"
                                className="i18n-table-loader-icon"
                            />
                        </div>
                    </Backdrop>
                    <DataGridTable
                        columns={columns}
                        marginTop="0px"
                        height="100%"
                        dataGridData={dataGridData}
                        clientId={currentUser?.client?.id}
                        rowModesModel={rowModesModel}
                        gridType="Internationalise"
                        handleUpdateProduct={handleUpdateRecord}
                        isCheckboxReq={false}
                        isRequiredToolBar={true}
                        showFilter={false}
                    />
                </Box>
            </Box>
        </Drawer>
    );
}

/**
 * Main I18nInspectorOverlay Component
 */
export default function I18nInspectorOverlay({ open, onClose }) {
    // Clear registry whenever route/page changes or tab query changes so keys don't leak across pages/tabs
    useEffect(() => {
        let lastLocation = history.location?.pathname + (history.location?.search || '');

        const unlisten = history.listen((location) => {
            const currentLocation = location.pathname + (location.search || '');
            if (currentLocation !== lastLocation) {
                lastLocation = currentLocation;
                i18nRegistry.clear();
            }
        });

        return unlisten;
    }, []);

    if (!open) return null;

    return (
        <PagePanel open={open} onClose={onClose} />
    );
}
